import { useCallback, useEffect, useState } from 'react';
import { type AiRead, api, clearSession, type Device, type Profile as ProfileData, type Quota } from '../lib/api';
import { idp, webauthn } from '@wa/auth';

const when = (iso: string) => new Date(iso).toLocaleDateString(undefined, {
  year: 'numeric', month: 'short', day: 'numeric',
});

/** Gigabytes once there are gigabytes; megabytes while there are not. */
const size = (n: number) => (n >= 1024 ** 3
  ? `${(n / 1024 ** 3).toFixed(2)} GB`
  : `${(n / 1024 ** 2).toFixed(1)} MB`);

const providerName: Record<string, string> = {
  google: 'Google', apple: 'Apple', passkey: 'Passkey', dev: 'Development sign-in',
};

export function PersonIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M12 13.4c-3.8 0-6.9 2.3-6.9 5.1V21h13.8v-2.5c0-2.8-3.1-5.1-6.9-5.1z" />
    </svg>
  );
}

/**
 * Everything the account is, in one panel: who the person is to us, which
 * identities can prove it, and which devices hold a key.
 *
 * The distinction the panel has to carry is the one from the threat model —
 * an identity signs you in, a passkey opens the archive. Revoking the last
 * passkey is therefore not a small act, and the copy says so.
 */
export function Profile({ onClose, onChange, accountKey, quota, inline = false,
                         start }: {
  onClose: () => void; onChange: () => void; accountKey: Uint8Array;
  /**
   * Onde abrir, quando quem manda abrir já sabe o que se procura.
   *
   * O Ask desligado diz "abra a sua conta" e deixava a pessoa no menu, com
   * cinco linhas — duas das quais falam de ler. Quem vem do Ask quer a da
   * IA, e é nela que aterra.
   */
  start?: View;
  /** Vem do App, que já o tem: o painel não precisa perguntar de novo. */
  quota: Quota;
  /**
   * Dentro da barra lateral em vez de por cima de tudo.
   *
   * A conta era uma folha modal que cobria a conversa que a pessoa estava
   * lendo — para ver quanto espaço restava era preciso sair de onde se estava.
   * Na coluna, ela é um painel como os outros. O conteúdo é o mesmo, e é por
   * isso que a classe interna continua sendo `.sheet-body`: todo o estilo dos
   * filhos pende dela.
   */
  inline?: boolean;
}) {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [shredText, setShredText] = useState('');
  const [swept, setSwept] = useState<string | null>(null);
  const [reads, setReads] = useState<AiRead[] | null>(null);
  /** Pedir antes de enviar, que é o que a regra exige e o que é decente. */
  const [askingAi, setAskingAi] = useState(false);
  /* O painel deixou de ser uma página só e virou um menu com telas, como a
     aba "You" do WhatsApp: o que estava numa rolagem de dois metros agora
     cabe em cinco linhas, e cada assunto abre onde pode respirar. */
  const [view, setView] = useState<View>(start ?? 'menu');
  const [googleAvailable, setGoogleAvailable] = useState<
    { apiKey: string; authDomain: string; projectId: string; appId: string } | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([api.profile(), api.devices()]);
      setProfile(p); setName(p.displayName ?? ''); setDevices(d.devices);
    } catch (e) {
      setError(String((e as Error).message ?? e));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  // The log, always — an empty one is the most useful thing it can say, and
  // fetching it only after the switch is on would hide the reads of someone
  // who has since turned it off.
  useEffect(() => { api.aiReads().then((r) => setReads(r.reads)).catch(() => setReads([])); }, []);
  useEffect(() => {
    api.authConfig()
      .then((c) => setGoogleAvailable(c.googleAvailable ? c.firebase : null))
      .catch(() => setGoogleAvailable(null));
  }, []);

  const act = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null);
    try { await fn(); await load(); onChange(); }
    catch (e) { setError(String((e as Error).message ?? e)); }
    finally { setBusy(false); }
  }, [load, onChange]);

  const linked = new Set((profile?.identities ?? []).map((i) => i.provider));

  const shell = (body: React.ReactNode, title?: string) => (
    <Shell inline={inline}>
      <div className={`sheet-body${inline ? ' inline' : ''}`}>
        <header>
          {title ? (
            <>
              <button className="pf-back" onClick={() => setView('menu')}>
                <span aria-hidden="true">‹</span> Account
              </button>
              <h2>{title}</h2>
            </>
          ) : <h2>Your account</h2>}
          {!inline && <button className="x" onClick={onClose} aria-label="Close">×</button>}
        </header>
        {body}
        {error && <p className="err">{error}</p>}
      </div>
    </Shell>
  );

  if (!profile) return shell(<p className="fine">Loading…</p>);

  if (view === 'account') return shell(<>
    <section className="who">
      {profile.avatarUrl
        ? <img className="big-avatar" src={profile.avatarUrl} alt="" referrerPolicy="no-referrer" />
        : <div className="big-avatar">{profile.displayName
            ? <span>{profile.displayName.slice(0, 1).toUpperCase()}</span>
            : <PersonIcon />}</div>}
      <div className="fields">
        <label htmlFor="display-name">Display name</label>
        <div className="row">
          <input id="display-name" value={name} maxLength={128}
                 onChange={(e) => setName(e.target.value)} />
          <button disabled={busy || name.trim() === (profile.displayName ?? '')}
                  onClick={() => act(() => api.setDisplayName(name.trim()))}>Save</button>
        </div>
        {/* Não é só um rótulo: um export escreve o seu nome de perfil do
            WhatsApp em cada mensagem sua, então é por este campo que o
            leitor sabe quais mensagens são suas e as põe à direita. */}
        <p className="fine">Also how your own messages are recognised in an imported
           chat — match the name WhatsApp shows on your messages.</p>
        <p className="fine">{profile.email ?? 'No email on file'} · {profile.homeRegion} ·
           joined {when(profile.createdAt)}</p>
      </div>
    </section>
    <section>
      <h3>Sign-in</h3>
      <p className="fine">These prove who you are. They cannot decrypt anything —
         that is what a passkey is for.</p>
      <ul className="rows">
        {profile.identities.map((i) => (
          <li key={i.provider}>
            <div>
              <strong>{providerName[i.provider] ?? i.provider}</strong>
              <span className="fine">{i.email ?? 'no email'}
                {i.emailVerified ? ' · verified' : ''} · last used {when(i.lastSeenAt)}</span>
            </div>
          </li>
        ))}
        {profile.identities.length === 0 && <li className="fine">Passkey only.</li>}
      </ul>
      {googleAvailable && !linked.has('google') && (
        <>
          <button className="ghost" disabled={busy} onClick={() => act(async () => {
            // Null means the page left for Google; App picks the link up on
            // the way back, by the intent stored with it.
            const idToken = await idp.signIn(googleAvailable, 'google', 'link');
            if (idToken) await api.linkIdentity(idToken);
          })}>Link a Google account</button>
          {/* The honest reason to link one — see D-006. Recovery needs
              something the server recognises before the words can be
              used, and with every passkey gone there is nothing. */}
          <p className="fine">Worth doing: if you ever lose every passkey, a linked
             account is what lets you present your recovery code at all.</p>
        </>
      )}
    </section>
    <section>
      <h3>Keys and devices</h3>
      <p className="fine">
        {profile.passkeyCount === 1
          ? 'One passkey can open your archive. If you lose it, only your recovery code remains.'
          : `${profile.passkeyCount} passkeys can open your archive.`}
      </p>
      <ul className="rows">
        {(devices ?? []).map((d) => (
          <li key={d.deviceId}>
            <div>
              <strong>{d.label ?? d.platform}</strong>
              <span className="fine">added {when(d.createdAt)} · last seen {when(d.lastSeenAt)}</span>
            </div>
            <button className="ghost small" disabled={busy}
                    onClick={() => act(() => api.revokeDevice(d.deviceId))}>Sign out</button>
          </li>
        ))}
        {devices?.length === 0 && <li className="fine">No devices signed in.</li>}
      </ul>
    </section>
  </>, 'Account');

  if (view === 'privacy') return shell(<>
    <section>
      <h3>Who holds the key</h3>
      {profile.keyHeldByService ? (
        <>
          <p className="fine">
            <strong>We can.</strong> We hold the key that decrypts your messages, which
            is what lets you sign in anywhere with just Google and never lose access.
          </p>
          <p className="fine">
            To end that, add a passkey to this device first — then the key becomes
            yours alone, and nobody, including us, can recover it for you.
          </p>
          <div className="row gap">
            <button className="ghost" disabled={busy} onClick={() => act(async () => {
              await webauthn.addPasskeyToAccount(
                (path: string, body: unknown) => api.post(path, body), accountKey);
            })}>Add a passkey to this device</button>
            <button className="ghost" disabled={busy || profile.passkeyCount === 0}
                    onClick={() => act(() => api.dropHeldKey())}>
              Stop holding my key
            </button>
          </div>
        </>
      ) : (
        <p className="fine">
          <strong>Only you can.</strong> The key lives on your devices and we have never
          held a copy. If you lose every passkey and your recovery code, the archive
          cannot be recovered by anyone.
        </p>
      )}
    </section>
  </>, 'Privacy');

  if (view === 'ai') return shell(<>
    <section>
      {/* Nomear quem recebe, e dizer o que é enviado, antes de enviar.
          "a model, through us" não dizia nem uma coisa nem outra: quem lia
          aquilo não ficava a saber que a conversa vai para a Google. */}
      <p className="fine">Answering a question means sending the messages of
         the conversation you ask about, and your question, to
         <strong> Google</strong> — the model is Gemini 2.5 Flash, called
         through Google Vertex AI in the United States. We pass it through and
         keep no copy. Everything that is read is listed below.</p>
      <p className="fine">Your archive stays encrypted and unreadable to us in
         every other part of the app. This is the one exception, and it only
         happens while this is on. <a href="/privacy" target="_blank"
         rel="noopener">Privacy policy</a>.</p>
      <button className={profile.aiEnabled ? 'ghost' : ''} disabled={busy}
              onClick={() => (profile.aiEnabled
                ? act(() => api.setAiEnabled(false))
                : setAskingAi(true))}>
        {profile.aiEnabled ? 'Turn off AI features' : 'Turn on AI features'}
      </button>

      {askingAi && (
        /* Desligar não precisa de permissão; ligar precisa. O botão sozinho
           não é um pedido — é por isso que isto existe. */
        <div className="sheet" role="dialog" aria-modal="true"
             aria-label="Turn on AI features?"
             onClick={(e) => { if (e.target === e.currentTarget) setAskingAi(false); }}>
          <div className="sheet-body ai-consent">
            <header>
              <h2>Send your messages to Google?</h2>
              <button className="x" onClick={() => setAskingAi(false)}
                      aria-label="Cancel">×</button>
            </header>
            <p className="fine">Turning this on lets AI features send the
               following out of this device, readable, each time you use one:</p>
            <ul className="plain">
              <li>The messages of the conversation you are asking about.</li>
              <li>Your question, and the earlier ones in that thread.</li>
              <li>The titles of your conversations, so a question can name one.</li>
            </ul>
            <p className="fine">It goes to <strong>Google</strong> — the model
               is <strong>Gemini 2.5 Flash</strong>, through Google Vertex AI,
               in the United States — which processes it to produce the answer
               under terms that provide for customer data not to be used to
               train its models. We keep no copy of what is sent; we record
               only that a read happened, and show you that list here.</p>
            <p className="fine">Nothing is sent until you agree, and you can
               turn this off again at any time.
               <a href="/privacy" target="_blank" rel="noopener"> Privacy
               policy</a>.</p>
            <div className="row gap">
              <button className="ghost" onClick={() => setAskingAi(false)}>Cancel</button>
              <button disabled={busy} onClick={() => {
                setAskingAi(false);
                void act(() => api.setAiEnabled(true));
              }}>I agree, turn it on</button>
            </div>
          </div>
        </div>
      )}

      <h4 className="logtitle">What has been read</h4>
      {reads === null ? <p className="fine">Loading…</p>
       : reads.length === 0 ? (
         <p className="fine">Nothing yet. This list stays empty until a feature
            reads something, and every read adds a line.</p>
       ) : (
         <ul className="rows reads">
           {reads.map((r) => (
             <li key={r.readId}>
               <div>
                 <strong>{r.feature === 'summary' ? 'Summary' : r.feature}</strong>
                 <span className="fine">
                   {r.messageCount.toLocaleString()} messages ·{' '}
                   {new Date(r.at).toLocaleString()} · {r.model}
                   {r.outcome !== 'ok' && ` · ${r.outcome}`}
                 </span>
               </div>
             </li>
           ))}
         </ul>
       )}
    </section>
  </>, 'AI features');

  if (view === 'storage') return shell(<>
    <section>
      <h3>Storage</h3>
      <div className="bar">
        <i style={{ width: `${Math.min(100, (quota.bytesUsed / quota.bytesLimit) * 100)}%` }} />
      </div>
      <p className="usage">
        <strong>{size(quota.bytesUsed)}</strong> of {size(quota.bytesLimit)} used
      </p>
      {/* "objects" é o que o servidor conta: pedaços de conversa e
          anexos, todos cifrados. Não são mensagens, e chamá-los de
          mensagens daria um número que não bate com nada na tela. */}
      <p className="fine">{quota.memberCount.toLocaleString()} encrypted files ·
         {' '}{size(quota.bytesLimit - quota.bytesUsed)} free</p>
      {/* Conversas removidas antes de o arquivo registrar a quem cada
          arquivo pertencia deixaram bytes para trás. Isto varre. */}
      <button className="ghost" disabled={busy} onClick={() => act(async () => {
        const r = await api.reclaim();
        setSwept(r.spacePending
          ? 'Nothing could be freed yet: open each remaining conversation once, so the archive learns what belongs to it, then try again.'
          : r.freedBytes
            ? `${size(r.freedBytes)} came back — ${r.packsRemoved} storage file${r.packsRemoved === 1 ? '' : 's'} removed.`
            : 'Nothing to reclaim; every byte here belongs to a conversation.');
      })}>Reclaim unused space</button>
      {swept && <p className="fine">{swept}</p>}
    </section>
  </>, 'Storage and data');

  if (view === 'danger') return shell(<>
    <section>
      <h3>This browser</h3>
      <button className="ghost" onClick={async () => {
        await idp.signOut().catch(() => {});
        clearSession();
        localStorage.clear();
        location.reload();
      }}>Sign out of this browser</button>
      <p className="fine">Your archive stays where it is. You will need a passkey
         or your recovery code to open it again.</p>
    </section>
    <section className="danger">
      <h3>Delete this archive</h3>
      <p className="fine">
        Every message, photo and voice note in <strong>this account</strong>, gone.
        The keys are destroyed first, so it stops being readable immediately —
        by you, by us, by anyone. There is no undo and no copy.
      </p>
      {profile.identities.length === 0 && (
        <p className="fine">This account signs in with a passkey only. Deleting it
           does not touch any other account you have.</p>
      )}
      {!confirming ? (
        <button className="ghost danger-btn" onClick={() => setConfirming(true)}>
          Delete this archive…
        </button>
      ) : (
        <div className="confirm-delete">
          <label htmlFor="shred">Type <strong>DELETE</strong> to confirm</label>
          <div className="row">
            <input id="shred" autoComplete="off" value={shredText}
                   onChange={(e) => setShredText(e.target.value)} />
            <button className="danger-btn" disabled={busy || shredText !== 'DELETE'}
                    onClick={() => act(async () => {
                      await api.shredAccount();
                      clearSession();
                      localStorage.clear();
                      location.reload();
                    })}>
              {busy ? 'Deleting…' : 'Delete permanently'}
            </button>
            <button className="ghost" disabled={busy}
                    onClick={() => { setConfirming(false); setShredText(''); }}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  </>, 'Leaving');

  // ── o menu ──────────────────────────────────────────────────────────
  const row = (to: View, label: string, note: string, icon: React.ReactNode,
               danger = false) => (
    <button className={`pf-row${danger ? ' danger' : ''}`} onClick={() => setView(to)}>
      <span className="ic" aria-hidden="true">{icon}</span>
      <span className="lbl"><strong>{label}</strong><span className="fine">{note}</span></span>
      <span className="go" aria-hidden="true">›</span>
    </button>
  );

  return shell(<>
    {/* Quem você é, grande, antes de qualquer configuração — é o que a
        pessoa veio confirmar quando abre isto. */}
    <div className="pf-hero">
      {profile.avatarUrl
        ? <img className="big-avatar" src={profile.avatarUrl} alt="" referrerPolicy="no-referrer" />
        : <div className="big-avatar">{profile.displayName
            ? <span>{profile.displayName.slice(0, 1).toUpperCase()}</span>
            : <PersonIcon />}</div>}
      <h3>{profile.displayName ?? 'Your account'}</h3>
      <p className="fine">{profile.email ?? 'No email on file'}</p>
    </div>

    <div className="pf-card">
      {row('account', 'Account', 'Name, sign-in, devices', (
        <svg viewBox="0 0 24 24"><circle cx="12" cy="8.5" r="3.5" /><path d="M5 20c0-3.6 3.1-5.5 7-5.5s7 1.9 7 5.5" /></svg>
      ))}
      {row('privacy', 'Privacy', profile.keyHeldByService
        ? 'We hold the key — and you can take it back'
        : 'Only you hold the key', (
        <svg viewBox="0 0 24 24"><rect x="5" y="10.5" width="14" height="9.5" rx="2.5" /><path d="M8.5 10.5V7.8a3.5 3.5 0 0 1 7 0v2.7" /></svg>
      ))}
      {row('ai', 'AI features', profile.aiEnabled
        ? `On · ${reads?.length ?? 0} read${reads?.length === 1 ? '' : 's'} logged`
        : 'Off', (
        <svg viewBox="0 0 24 24"><path d="M12 3.6l1.6 3.9 3.9 1.6-3.9 1.6L12 14.6l-1.6-3.9L6.5 9.1l3.9-1.6z" /><path d="M18 15.6l.7 1.7 1.7.7-1.7.7-.7 1.7-.7-1.7-1.7-.7 1.7-.7z" /></svg>
      ))}
      {row('storage', 'Storage and data', `${size(quota.bytesUsed)} of ${size(quota.bytesLimit)} used`, (
        <svg viewBox="0 0 24 24"><path d="M7 4v13M7 20l-3-3M7 20l3-3M17 20V7M17 4l-3 3M17 4l3 3" /></svg>
      ))}
    </div>

    <div className="pf-card">
      {row('danger', 'Sign out or delete', 'Leave this browser, or destroy the archive', (
        <svg viewBox="0 0 24 24"><path d="M15 5.5H6.5A1.5 1.5 0 0 0 5 7v10a1.5 1.5 0 0 0 1.5 1.5H15" /><path d="M14 12h6M17.5 8.5L21 12l-3.5 3.5" /></svg>
      ), true)}
    </div>
  </>);
}

type View = 'menu' | 'account' | 'privacy' | 'ai' | 'storage' | 'danger';

/** Uma folha modal, ou nada — o painel embutido não quer nem fundo nem foco. */
function Shell({ inline, children }: { inline: boolean; children: React.ReactNode }) {
  if (inline) return <>{children}</>;
  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label="Your account">
      {children}
    </div>
  );
}
